import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, LayoutDashboard, UserCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AccountMenu() {
  const { customerToken, customerInfo, logoutCustomer } = useApp();
  const navigate = useNavigate();

  if (!customerToken) {
    return (
      <Link
        to="/account/login"
        data-testid="header-signin-link"
        className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-secondary hover:bg-muted sm:inline-flex"
      >
        <User className="h-3.5 w-3.5" />
        Sign in
      </Link>
    );
  }

  const source = customerInfo?.name || customerInfo?.email || "?";
  const initials = source
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const doLogout = () => {
    logoutCustomer();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="header-account-menu"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-label="Account"
      >
        <span className="text-xs font-semibold">{initials || "•"}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm font-semibold text-secondary">
            {customerInfo?.name || "Signed in"}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {customerInfo?.email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/account"
            data-testid="account-menu-dashboard"
            className="flex w-full cursor-pointer items-center gap-2"
          >
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to="/account/profile"
            data-testid="account-menu-profile"
            className="flex w-full cursor-pointer items-center gap-2"
          >
            <UserCircle className="h-4 w-4" /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={doLogout}
          data-testid="account-menu-logout"
          className="cursor-pointer text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
